import { useState } from 'react'

import { Dropzone, MessagePopup, UploadedFileZone } from '@skytech/ui'
import { useQuery } from '@tanstack/react-query'

import { Translation } from '@/app/translations/Translation'
import { commonDocumentResources } from '@/resources/document'
import { commonQueries } from '@/resources/queries'
import { DOCUMENT_TYPE } from '@/util/dropzone/constants'
import useAuthFilesDropzone from '@/util/dropzone/useAuthFilesDropzone'

import { useNewSubscriptionContext } from './NewSubscriptionForm.context'

const NewSubscriptionAuthorization = () => {
  const [uploadError, setUploadError] = useState('')
  const [isErrorPopupOpen, setIsErrorPopupOpen] = useState(false)

  const [authorizationDocumentId, setAuthorizationDocumentId] =
    useNewSubscriptionContext().authorizationDocumentId
  const [authorizationDocumentFilename, setAuthorizationDocumentFilename] =
    useNewSubscriptionContext().authorizationDocumentFilename

  const { authorizeUpload, isAuthorizingUpload } = useAuthFilesDropzone({
    type: DOCUMENT_TYPE.ONBOARDING_AUTH_FORM,
    fileTypes: ['pdf', 'png', 'msg', 'docx'],
    onUpload: res => {
      setUploadError('')
      setAuthorizationDocumentId(res.id)
      setAuthorizationDocumentFilename(res.filename)
    },
    onError: setUploadError,
  })

  const { refetch: authorizeFileDownload, isInitialLoading: isDownloading } = useQuery(
    authorizationDocumentId
      ? commonQueries.documentDownloadAuthorization(authorizationDocumentId)
      : [''],
    () => {
      if (authorizationDocumentId)
        return commonDocumentResources.authorizeFileDownload(authorizationDocumentId)
    },
    {
      enabled: false,
      onError() {
        setIsErrorPopupOpen(true)
      },
    }
  )

  return (
    <div className="new-sub-auth">
      <h3>
        <Translation id="Authorization" />
      </h3>

      <p>
        <Translation
          id="If you already have a signed power of attorney from the old owner of the subscription, you
        can upload it here. This means that the order will not be sent to the end user, but sent
        directly to the dealer. Then it is important that in the next step you fill in the address
        of the person so that he or she is sent a new SIM card."
        />
      </p>

      {authorizationDocumentId ? (
        <UploadedFileZone
          documentFilename={authorizationDocumentFilename}
          isDownloading={isDownloading}
          onDownloadClick={authorizeFileDownload}
          onRemoveClick={() => setAuthorizationDocumentId(null)}
        />
      ) : (
        <Dropzone
          id="new-sub-dropzone"
          activeDragText={<Translation id="Release file here to upload" />}
          inActiveDragText={<Translation id="Drag file here or click to browse" />}
          customError={uploadError}
          isUploading={isAuthorizingUpload}
          onDrop={authorizeUpload}
        />
      )}

      <MessagePopup
        title={<Translation id="File download error" />}
        message={
          <Translation id="We encountered a problem while downloading your file. Please refresh and try again." />
        }
        isOpen={isErrorPopupOpen}
        onClose={() => setIsErrorPopupOpen(false)}
      />
    </div>
  )
}

export default NewSubscriptionAuthorization
